/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  Heading,
  HStack,
  Flex,
  useDisclosure,
} from '@chakra-ui/react';
import React, { forwardRef, useImperativeHandle } from 'react';
import { Text } from '../../../components/atoms/Text';
import { Button } from '../../../components/atoms/Button';
import { Input, InputArea } from '../../../components/atoms/Input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useMutation } from 'react-query';
import { useProfile } from '@/contexts/ProfileProvider';
import { createSale } from '@/service/club/api';
import { IPromotionCard } from '@/service/club/types';
import { masks } from '@/utils/masks';

const Thumb = ({
  onClick,
  direction = 'up',
  color = 'green',
  active,
}: {
  onClick: () => void;
  direction: 'up' | 'down';
  color: string;
  active: boolean;
}) => (
  <Flex
    cursor="pointer"
    onClick={onClick}
    border="1px solid rgba(0,0,0,0.2)"
    borderRadius="10px"
    padding="20px"
    backgroundColor="#ffffff"
  >
    <i
      style={{
        color: active ? color : 'rgba(0,0,0,0.2)',
        fontSize: 25,
        transition: '0.2s',
      }}
      className={`fas fa-thumbs-${direction}`}
    ></i>
  </Flex>
);

export const RateExperienceModal = forwardRef(function (
  { companyName, discount }: { companyName?: string; discount?: IPromotionCard },
  ref
) {
  const { profile } = useProfile();
  const [rating, setRating] = React.useState(true);
  const rateDisclosure = useDisclosure();
  const [code, setCode] = React.useState<any>();

  const formSchema = z.object({
    value: z.string().min(1),
    reason: z.string().optional(),
  });

  const formNegativeSchema = z.object({
    reason: z.string().min(10, 'O Motivo deve ter pelo menos 10 caracteres reais.'),
    telefone: z
      .string()
      .regex(/^\(\d{2}\) \d{4,5}-\d{4}$/, 'Telefone inválido. O formato deve ser (XX) XXXX-XXXX ou (XX) XXXXX-XXXX'),
  });

  const methods = useForm({
    resolver: zodResolver(rating ? formSchema : formNegativeSchema),
  });

  const createMutation = useMutation(({ value, reason, telefone }: any) => {
    const formattedReason = reason?.replace(/\s+/g, ' ').trim();
    const unformatedTelefone = telefone?.replace(/\D/g, '');

    if (!rating && formattedReason.length < 10) {
      methods.setError('reason', {
        message: 'O Motivo deve ter pelo menos 10 caracteres reais.',
      });
    }

    if (!rating || unformatedTelefone?.length < 11) {
      methods.setError('telefone', {
        message: 'O Telefone é obrigatório.',
      });
    }

    const payment: any = {
      feedback: rating,
      user_given_discount: value ? masks.unmask(value) / 100 : 0,
      discount: discount?.id,
      virtual_card: code || undefined,
    };

    if (!rating) {
      payment.reason = formattedReason;
      payment.telefone = telefone;
    }

    return createSale(payment).then(() => {
      rateDisclosure.onClose();
    });
  });

  const { control, setValue, handleSubmit } = methods;

  useImperativeHandle(
    ref,
    () => ({
      open: (code?: string) => {
        if (profile?.telephone) {
          setValue('telefone', masks.cell(profile?.telephone));
        }
        setCode(code);
        rateDisclosure.onOpen();
        setValue('value', discount?.originalPrice);
      },
      close: () => {
        rateDisclosure.onClose();
      },
    }),
    [rateDisclosure, setValue, discount, profile]
  );

  return (
    <Modal onClose={rateDisclosure.onClose} isCentered isOpen={rateDisclosure.isOpen}>
      <ModalOverlay />
      <ModalContent margin={'20px'} borderRadius={'16px'}>
        <ModalBody>
          <form
            onSubmit={handleSubmit(({ value, reason, telefone }) => createMutation.mutate({ value, reason, telefone }))}
            style={{ width: '100%' }}
          >
            <VStack spacing={4} padding={'20px 10px'}>
              <Heading fontFamily={'Glory'} fontWeight="700" textAlign={'center'} fontSize={25}>
                Como foi seu desconto {companyName} ?
              </Heading>
              <Text textAlign={'center'} color="#747474" fontSize={16} fontWeight={400} fontFamily={'Glory'}>
                Seu feedback nos ajuda a manter a boa qualidade Partiu.
              </Text>
              <HStack spacing={2}>
                <Thumb onClick={() => setRating(true)} active={rating} color="green" direction="up" />
                <Thumb onClick={() => setRating(false)} active={!rating} color="red" direction="down" />
              </HStack>

              {rating ? (
                <Input mask="currency" label="Valor Economizado" name="value" control={control} />
              ) : (
                <>
                  <InputArea label="Motivo" name="reason" control={control} />
                  <Input mask="cell" label="Telefone" name="telefone" control={control} />
                </>
              )}
              <VStack width={'100%'} spacing={2}>
                <Button width="100%" type="submit" isLoading={createMutation.isLoading}>
                  Confirmar
                </Button>
                <Button
                  borderColor={'#242424'}
                  isLoading={createMutation.isLoading}
                  backgroundColor="white"
                  color={'#242424'}
                  width="35%"
                  variant="ghost"
                  onClick={rateDisclosure.onClose}
                >
                  Cancelar
                </Button>
              </VStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});
